import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api/client';
import { usePermission } from '../../hooks/usePermission';
import { useToast } from '../../components/ui/Toast';
import { date, money } from '../../utils/format';
import { Chip, Empty, ErrorNotice, Loading, PageHeader, Table } from '../../components/ui';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';

interface EstimateRow {
  id: string;
  doc_no: string;
  doc_date: string;
  party_name: string;
  status: 'OPEN' | 'CONVERTED' | 'EXPIRED' | 'CANCELLED';
  grand_total: string;
  valid_until: string | null;
  sale_doc_no: string | null;
  pieces: string;
}

const STATUS: Record<EstimateRow['status'], { tone: 'good' | 'warn' | 'bad' | 'mute'; label: string }> = {
  OPEN: { tone: 'warn', label: 'open' },
  CONVERTED: { tone: 'good', label: 'billed' },
  EXPIRED: { tone: 'mute', label: 'expired' },
  CANCELLED: { tone: 'mute', label: 'cancelled' },
};

/**
 * Estimates — the quotes a shop has given, and the one action that matters on
 * each: turning it into a bill.
 *
 * A quote sits here until the customer decides. When they buy, Convert runs the
 * real sale at the price they were quoted; the estimate is then marked billed
 * and links to the invoice it became, so there is a clear trail from promise to
 * sale and no way to bill the same quote twice.
 */
export function EstimatesPage() {
  const { can } = usePermission();
  const client = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [error, setError] = useState<unknown>(null);
  const [confirming, setConfirming] = useState<EstimateRow | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['estimates'],
    queryFn: () => api.get<{ data: EstimateRow[] }>('/estimates'),
  });

  const convert = useMutation({
    mutationFn: (id: string) =>
      api.post<{ saleId: string; docNo: string }>(`/estimates/${id}/convert`, { payments: [] }),
    onSuccess: (r) => {
      toast(`Billed as ${r.docNo}`, 'The quote is now a real invoice');
      void client.invalidateQueries({ queryKey: ['estimates'] });
      void client.invalidateQueries({ queryKey: ['sales'] });
      navigate(`/sales/${r.saleId}/print`);
    },
    onError: setError,
  });

  const rows = data?.data ?? [];

  return (
    <>
      <PageHeader
        title="Estimates"
        subtitle="Quotes given to customers. Convert one to a bill when they decide to buy."
        actions={
          can('estimate.create') ? (
            <button className="btn-primary" onClick={() => navigate('/pos')}>
              New estimate
            </button>
          ) : null
        }
      />
      <ErrorNotice error={error} />

      {isLoading ? <Loading /> : null}
      {!isLoading && rows.length === 0 ? (
        <Empty
          message="No estimates yet."
          hint="An estimate is a saved quote — same pricing as a bill, but nothing is charged and no stock moves until you convert it. Make one from the Sell screen with the Estimate toggle."
          action={can('estimate.create') ? { label: 'Go to Sell', onClick: () => navigate('/pos') } : undefined}
        />
      ) : null}

      {rows.length > 0 ? (
        <Table head={['Date', 'Estimate', 'Customer', 'Pieces', 'Total', 'Status', 'Billed as', '']}>
          {rows.map((e) => {
            const s = STATUS[e.status];
            return (
              <tr key={e.id}>
                <td className="td">{date(e.doc_date)}</td>
                <td className="td num">{e.doc_no}</td>
                <td className="td">{e.party_name}</td>
                <td className="td num text-right">{e.pieces}</td>
                <td className="td num text-right font-medium">{money(e.grand_total)}</td>
                <td className="td">
                  <Chip tone={s.tone}>{s.label}</Chip>
                  {e.status === 'OPEN' && e.valid_until ? (
                    <span className="ml-1 text-2xs text-ink-faint">till {date(e.valid_until)}</span>
                  ) : null}
                </td>
                <td className="td num text-2xs">{e.sale_doc_no ?? '—'}</td>
                <td className="td text-right">
                  <button
                    className="mr-2 text-2xs text-gold-700 underline"
                    onClick={() => navigate(`/estimates/${e.id}/print`)}
                  >
                    Print / PDF
                  </button>
                  {e.status === 'OPEN' && can('estimate.convert') ? (
                    <button
                      className="btn-primary py-1 text-xs"
                      disabled={convert.isPending}
                      onClick={() => setConfirming(e)}
                    >
                      Convert to bill
                    </button>
                  ) : null}
                </td>
              </tr>
            );
          })}
        </Table>
      ) : null}

      <ConfirmDialog
        open={!!confirming}
        tone="primary"
        title="Turn this estimate into a bill?"
        message="A real invoice is posted, the stock moves, and the books are updated. The estimate keeps the price it was quoted at."
        detail={
          confirming ? (
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
              <dt className="text-ink-soft">Estimate</dt>
              <dd className="num">{confirming.doc_no}</dd>
              <dt className="text-ink-soft">Customer</dt>
              <dd className="font-medium">{confirming.party_name}</dd>
              <dt className="text-ink-soft">Pieces</dt>
              <dd className="num">{confirming.pieces}</dd>
              <dt className="text-ink-soft">To bill</dt>
              <dd className="num text-base font-bold">{money(confirming.grand_total)}</dd>
            </dl>
          ) : null
        }
        confirmLabel="Bill it"
        busy={convert.isPending}
        onCancel={() => setConfirming(null)}
        onConfirm={() => {
          if (confirming) convert.mutate(confirming.id);
          setConfirming(null);
        }}
      />
    </>
  );
}
